import React, { useEffect, useRef } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import 'webrtc-adapter'; // shim for older browsers / webviews

const QrReader = ({ onScan, onError, style }) => {
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const scannedRef = useRef(false); // track if we already scanned once
  const erroredRef = useRef(false); // track if we already hit a fatal error

  useEffect(() => {
    if (
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== 'function'
    ) {
      const err = new Error(
        'Camera not available: navigator.mediaDevices.getUserMedia is missing.'
      );
      alert(err.message);
      onError?.(err);
      return;
    }

    const codeReader = new BrowserMultiFormatReader();
    codeReaderRef.current = codeReader;

    codeReader
      .decodeFromVideoDevice(
        undefined, // use default camera
        videoRef.current,
        (result, err) => {
          // Handle successful scan once
          if (result && !scannedRef.current && !erroredRef.current) {
            scannedRef.current = true; // mark as scanned
            onScan?.(result.getText());

            // Stop scanning immediately after a successful result
            stopCamera(codeReader);
            return;
          }

          // Handle errors
          if (err && !scannedRef.current && !erroredRef.current) {
            const ignorableErrors = [
              'NotFoundException',
              'ChecksumException',
              'FormatException',
            ];

            // Ignore expected per-frame decode errors
            if (
              ignorableErrors.includes(err.name) ||
              err.message?.includes('No MultiFormat Readers')
            ) {
              return;
            }

            // First real error → stop scanning + camera
            erroredRef.current = true;
            stopCamera(codeReader);

            alert('Camera error: ' + err.message);
            onError?.(err);
          }
        }
      )
      .catch(err => {
        if (!erroredRef.current) {
          erroredRef.current = true;
          stopCamera(codeReader);

          alert('Failed to start camera: ' + err.message);
          onError?.(err);
        }
      });

    return () => {
      stopCamera(codeReader);
    };
  }, [onScan, onError]);

  // Helper to stop ZXing decoding loop and release camera tracks
  const stopCamera = (reader) => {
    try {
      // Reset cancels decode loop + clears state
      reader.reset();
    } catch {
      /* ignore */
    }

    // Stop the camera stream tracks so the light turns off
    const stream = videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  return (
    <video
      ref={videoRef}
      style={{ width: '100%', borderRadius: '4px', ...style }}
      muted
      playsInline
    />
  );
};

export default QrReader;
